import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { formatPrice } from "../lib/format";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const { t } = useTranslation();
  const category = params.get("category") || "all";
  const search = params.get("q") || "";
  const sort = params.get("sort") || "featured";
  const minP = parseFloat(params.get("min") || "0");
  const maxP = parseFloat(params.get("max") || "0");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(search);
  const [showFilters, setShowFilters] = useState(false);

  const SORTS = [
    { id: "featured", label: t("shop.sort_featured") },
    { id: "price_asc", label: t("shop.sort_price_asc") },
    { id: "price_desc", label: t("shop.sort_price_desc") },
    { id: "rating", label: t("shop.sort_best_rated") },
    { id: "newest", label: t("shop.sort_newest") },
  ];

  useEffect(() => {
    setLoading(true);
    api
      .get("/products", {
        params: {
          category: category === "all" ? undefined : category,
          search: search || undefined,
        },
      })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [category, search]);

  // Compute price bounds from raw products list
  const priceBounds = useMemo(() => {
    if (products.length === 0) return { min: 0, max: 200 };
    const prices = products.map((p) => p.price);
    return { min: Math.floor(Math.min(...prices)), max: Math.ceil(Math.max(...prices)) };
  }, [products]);

  // Filter + sort client-side
  const visible = useMemo(() => {
    let list = [...products];
    if (minP > 0) list = list.filter((p) => p.price >= minP);
    if (maxP > 0) list = list.filter((p) => p.price <= maxP);
    switch (sort) {
      case "price_asc":
        list.sort((a, b) => a.price - b.price);
        break;
      case "price_desc":
        list.sort((a, b) => b.price - a.price);
        break;
      case "rating":
        list.sort(
          (a, b) =>
            (b.avg_rating || 0) - (a.avg_rating || 0) ||
            (b.review_count || 0) - (a.review_count || 0)
        );
        break;
      case "newest":
        list.sort(
          (a, b) =>
            new Date(b.created_at || 0).getTime() -
            new Date(a.created_at || 0).getTime()
        );
        break;
      case "featured":
      default:
        list.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
        break;
    }
    return list;
  }, [products, sort, minP, maxP]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (!value || value === "all" || value === "featured" || value === "0") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setParams(next);
  };

  const setCategory = (c) => setParam("category", c);
  const setSort = (s) => setParam("sort", s);

  const submitSearch = (e) => {
    e.preventDefault();
    setParam("q", searchInput);
  };

  const resetFilters = () => {
    setParams(new URLSearchParams());
    setSearchInput("");
  };

  const hasActiveFilters =
    category !== "all" || search || sort !== "featured" || minP > 0 || maxP > 0;

  const cats = [
    { id: "all", label: t("shop.filter_all") },
    { id: "smart", label: t("shop.filter_smart") },
    { id: "wellness", label: t("shop.filter_wellness") },
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="shop-page">
      <div className="border-b border-white/10 pb-8 mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
            {t("shop.catalog")}
          </div>
          <h1 className="font-display mt-3 text-4xl sm:text-5xl font-bold uppercase tracking-tighter text-white">
            {category === "smart"
              ? "Smart Tech"
              : category === "wellness"
              ? "Home Office Wellness"
              : t("shop.all_products")}
          </h1>
          <div className="mt-3 text-sm text-white/60" data-testid="results-count">
            {visible.length} {visible.length > 1 ? t("shop.items_plural") : t("shop.items_singular")}
            {visible.length !== products.length && (
              <span className="text-white/40"> {t("shop.out_of", { total: products.length })}</span>
            )}
          </div>
        </div>

        <form onSubmit={submitSearch} className="relative w-full md:w-80">
          <Search
            size={16}
            strokeWidth={1.5}
            className="absolute left-0 top-1/2 -translate-y-1/2 text-white/60"
          />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t("common.search") + "..."}
            className="w-full pl-7 pr-2 py-2 border-b border-white/40 bg-transparent text-white text-sm focus:outline-none focus:border-white"
            data-testid="search-input"
          />
        </form>
      </div>

      {/* Toolbar: categories + sort + filters toggle */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {cats.map((c) => (
          <button
            key={c.id}
            onClick={() => setCategory(c.id)}
            className={`px-6 py-2 text-xs font-bold uppercase tracking-[0.25em] border transition-all ${
              category === c.id
                ? "bg-white text-[#001F3F] border-white"
                : "bg-transparent text-white/80 border-white/20 hover:border-white"
            }`}
            data-testid={`filter-${c.id}`}
          >
            {c.label}
          </button>
        ))}

        <div className="ml-auto flex items-center gap-3">
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-[#0A2A4F] text-white border border-white/20 px-3 py-2 text-xs uppercase tracking-widest font-bold focus:outline-none focus:border-white"
            data-testid="sort-select"
          >
            {SORTS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
          <button
            onClick={() => setShowFilters((v) => !v)}
            className={`inline-flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-[0.25em] border transition-all ${
              showFilters || minP > 0 || maxP > 0
                ? "bg-white text-[#001F3F] border-white"
                : "bg-transparent text-white/80 border-white/20 hover:border-white"
            }`}
            data-testid="toggle-filters"
          >
            <SlidersHorizontal size={14} /> {t("shop.filters")}
          </button>
        </div>
      </div>

      {/* Price range panel */}
      {showFilters && (
        <div
          className="border border-white/10 bg-[#0A2A4F] p-5 mb-6 flex flex-wrap items-end gap-4"
          data-testid="price-filter-panel"
        >
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.25em] text-[#C5CDD8] mb-2">
              {t("shop.price_min")}
            </label>
            <input
              type="number"
              min={priceBounds.min}
              max={priceBounds.max}
              value={minP || ""}
              onChange={(e) => setParam("min", e.target.value || "0")}
              placeholder={`${priceBounds.min}`}
              className="w-28 bg-[#001F3F] border border-white/20 text-white px-3 py-2 text-sm focus:outline-none focus:border-white"
              data-testid="price-min-input"
            />
          </div>
          <div className="text-white/40 pb-2">—</div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.25em] text-[#C5CDD8] mb-2">
              {t("shop.price_max")}
            </label>
            <input
              type="number"
              min={priceBounds.min}
              max={priceBounds.max}
              value={maxP || ""}
              onChange={(e) => setParam("max", e.target.value || "0")}
              placeholder={`${priceBounds.max}`}
              className="w-28 bg-[#001F3F] border border-white/20 text-white px-3 py-2 text-sm focus:outline-none focus:border-white"
              data-testid="price-max-input"
            />
          </div>
          <div className="text-xs text-white/50 pb-3">
            {t("shop.price_range")} : {formatPrice(priceBounds.min)} —{" "}
            {formatPrice(priceBounds.max)}
          </div>
        </div>
      )}

      {/* Active filters chips + reset */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2 mb-6 text-xs">
          {search && (
            <Chip onClear={() => setParam("q", "")}>« {search} »</Chip>
          )}
          {category !== "all" && (
            <Chip onClear={() => setCategory("all")}>
              {cats.find((c) => c.id === category)?.label}
            </Chip>
          )}
          {sort !== "featured" && (
            <Chip onClear={() => setSort("featured")}>
              {SORTS.find((s) => s.id === sort)?.label}
            </Chip>
          )}
          {minP > 0 && (
            <Chip onClear={() => setParam("min", "0")}>≥ {formatPrice(minP)}</Chip>
          )}
          {maxP > 0 && (
            <Chip onClear={() => setParam("max", "0")}>≤ {formatPrice(maxP)}</Chip>
          )}
          <button
            onClick={resetFilters}
            className="text-white/60 hover:text-white underline underline-offset-4 uppercase tracking-widest font-bold ml-2"
            data-testid="reset-filters"
          >
            {t("shop.clear_all")}
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-24 text-sm text-white/60">{t("common.loading")}</div>
      ) : visible.length === 0 ? (
        <div className="text-center py-24 text-sm text-white/60" data-testid="empty-products">
          {t("shop.no_results")}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12 stagger">
          {visible.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function Chip({ children, onClear }) {
  return (
    <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-white text-[#001F3F] font-bold uppercase tracking-widest">
      {children}
      <button
        onClick={onClear}
        aria-label="Retirer le filtre"
        className="hover:bg-[#001F3F] hover:text-white p-0.5"
      >
        <X size={11} />
      </button>
    </span>
  );
}
